/**
 * @Description 键盘类，处理键盘交互
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 */

import * as THREE from 'three'
import { Renderer } from './Renderer'
import { InputBase } from './Input/InputBase'
import { CPointSet } from './CameraPoints'
import { config } from './config'

class Keyboard extends InputBase {
  /**
   * 键盘构造函数
   * @param {Renderer} renderer 渲染器
   * @param {CPointSet} cps 点位集合
   */
  constructor (renderer, cps) {
    super()

    /**
     * @type {{W:boolean,S:boolean,A:boolean,D:boolean,UP:boolean,DOWN:boolean,LEFT:boolean,RIGHT:boolean,I:boolean,K:boolean,J:boolean,L:boolean}} 具体键值状态
     */
    this.keyboardControl = {
      W: false,
      S: false,
      A: false,
      D: false,
      UP: false,
      DOWN: false,
      LEFT: false,
      RIGHT: false,
      I: false,
      K: false,
      J: false,
      L: false
    }
    /**
     * @type {boolean} 键盘按键状态
     */
    this.keyStatus = 0

    /**
     * @type {number} 键盘控制相机旋转速度
     */
    this.keyRotateSpeed = 2.0

    /**
     * @type {number} 模型视角下相机平移速度
     */
    this.keyPanSpeed = 10

    /**
     * @type {number} 全景视角下键盘wsad跳转步长
     */
    this.walkingDistance = 5000

    /**
     * @type {Element} 用于监听事件的dom元素
     */
    this.eventElement = document

    /**
     * @type {Renderer} 渲染器，用于监听是否活动
     */
    this.renderer = renderer

    /**
     * @type {CPointSet} 点位集合
     */
    this.cps = cps

    /**
     * @type {function} 按下绑定的方法
     */
    this.downBind = this.onDocumentKeyDown.bind(this)
    /**
     * @type {function} 抬起绑定的方法
     */
    this.upBind = this.onDocumentKeyUp.bind(this)
  }

  /**
   * 初始化绑定监听
   */
  init () {
    // 键盘监听
    this.eventElement.addEventListener('keydown', this.downBind, false)
    this.eventElement.addEventListener('keyup', this.upBind, false)
  }

  /**
   * 废弃方法，去除监听器
   */
  depose () {
    this.eventElement.removeEventListener('keydown', this.downBind, false)
    this.eventElement.removeEventListener('keyup', this.upBind, false)
  }

  /**
   * 键盘按下事件处理，修改对应的键值为true，让controller进一步处理具体事件
   * @param {object} event 键盘按下事件
   */
  onDocumentKeyDown (event) {
    if (!this.renderer.active) return
    if (this.enable) {
      this.keyStatus = true
      switch (event.keyCode) {
        case 87:
          this.keyboardControl.W = true
          break
        case 83:
          this.keyboardControl.S = true
          break
        case 65:
          this.keyboardControl.A = true
          break
        case 68:
          this.keyboardControl.D = true
          break
        case 38:
          this.keyboardControl.UP = true
          break
        case 40:
          this.keyboardControl.DOWN = true
          break
        case 37:
          this.keyboardControl.LEFT = true
          break
        case 39:
          this.keyboardControl.RIGHT = true
          break
        case 73:
          this.keyboardControl.I = true
          break
        case 75:
          this.keyboardControl.K = true
          break
        case 74:
          this.keyboardControl.J = true
          break
        case 76:
          this.keyboardControl.L = true
          break
      }
    }
  }

  /**
   * 键盘弹起事件处理，修改对应的键值为false，中断controller的键盘事件处理
   * @param {object} event 键盘弹起事件
   */
  onDocumentKeyUp (event) {
    if (!this.renderer.active) return
    if (this.enable) {
      switch (event.keyCode) {
        case 87:
          this.keyboardControl.W = false
          break
        case 83:
          this.keyboardControl.S = false
          break
        case 65:
          this.keyboardControl.A = false
          break
        case 68:
          this.keyboardControl.D = false
          break
        case 38:
          this.keyboardControl.UP = false
          break
        case 40:
          this.keyboardControl.DOWN = false
          break
        case 37:
          this.keyboardControl.LEFT = false
          break
        case 39:
          this.keyboardControl.RIGHT = false
          break
        case 73:
          this.keyboardControl.I = false
          break
        case 75:
          this.keyboardControl.K = false
          break
        case 74:
          this.keyboardControl.J = false
          break
        case 76:
          this.keyboardControl.L = false
      }
    }
  }

  /**
   * 处理全景视角下的键盘事件（IKJL左右键控制视角，WSAD上下键控制点位跳转）
   */
  handleViewKeyEvents () {
    // 键盘全景视角处理输入
    if (this.keyboardControl.J || this.keyboardControl.LEFT) {
      this.dlon -= this.keyRotateSpeed
    }
    if (this.keyboardControl.L || this.keyboardControl.RIGHT) {
      this.dlon += this.keyRotateSpeed
    }
    if (this.keyboardControl.I) {
      this.dlat += this.keyRotateSpeed * 0.5
    }
    if (this.keyboardControl.K) {
      this.dlat -= this.keyRotateSpeed * 0.5
    }
  }

  /**
   * 全景视角下键盘操作实现点位跳转，WSAD分别对应前后左右
   * @param {number} angle 与摄像机方向偏转角度，跳转方向
   */
  keyMove (angle) {
    const dir = this.cam.target.clone()
      .sub(this.cam.camera.position)
      .applyEuler(new THREE.Euler(0, THREE.MathUtils.degToRad(angle), 0))

    const rls = InputBase.findInNeighbour(
      this.controller.camera.cameraCurrentIndex,
      this.cam.camera.position,
      dir, this.cps)

    if (rls.cpoint) {
      this.cam.cameraDestinationIndex = rls.cpoint.id
      this.controller.animateCamera(this.cam.camera.position, this.cam.cameraDestinationIndex)
    }
  }

  /**
   * 处理模型视角下的键盘事件（上下左右键IKJL控制rotate，WSAD控制panning）
   */
  handleGodKeyEvents () {
    // 键盘上帝视角处理输入
    if (this.keyboardControl.I || this.keyboardControl.UP) {
      this.dlat += this.keyRotateSpeed * 0.5
    }
    if (this.keyboardControl.K || this.keyboardControl.DOWN) {
      this.dlat -= this.keyRotateSpeed * 0.5
    }
    if (this.keyboardControl.J || this.keyboardControl.LEFT) {
      this.dlon += this.keyRotateSpeed
    }
    if (this.keyboardControl.L || this.keyboardControl.RIGHT) {
      this.dlon -= this.keyRotateSpeed
    }

    if (this.keyboardControl.W) {
      this.dtranslate.y = this.keyPanSpeed
    }
    if (this.keyboardControl.S) {
      this.dtranslate.y = -this.keyPanSpeed
    }
    if (this.keyboardControl.A) {
      this.dtranslate.x = this.keyPanSpeed
    }
    if (this.keyboardControl.D) {
      this.dtranslate.x = -this.keyPanSpeed
    }
  }

  /**
   * 相机状态更新
   */
  camUpdate () {
    if (this.enable) {
      if (this.cam.cameraMode) {
        this.handleViewKeyEvents()
      } else {
        this.handleGodKeyEvents()
      }
      this.activate = this.dlon !== 0 || this.dlat !== 0 ||
      this.dtranslate.x !== 0 || this.dtranslate.y !== 0 ||
      this.dzoom !== 0
    }
  }

  /**
   * 键盘实例更新
   */
  update () {
    if (this.cam.cameraMode) {
      if ((this.keyboardControl.W || this.keyboardControl.UP)) {
        this.keyMove(0)
      }
      if ((this.keyboardControl.S || this.keyboardControl.DOWN)) {
        this.keyMove(180)
      }
      if (this.keyboardControl.A) {
        this.keyMove(90)
      }
      if (this.keyboardControl.D) {
        this.keyMove(-90)
      }
    }
  }
}

export {
  Keyboard
}
